import React, { useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { createLead } from '../lib/api';
import { GYM_CONFIG } from '../config';
import membershipSales from '../config/membershipSales.json';
import { getLeadAttribution } from '../utils/attribution';
import { trackFormStart } from '../utils/analytics';
import { isEffectivePreview } from '../utils/previewSafety';
import { buildWaitlistLeadPayload, createLeadRequestId } from '../utils/leadContracts';
import { getLeadSubmissionErrorMessage } from '../utils/leadSubmission';

const WAITLIST = membershipSales.waitlist;

const emptyForm = () => ({ first_name: '', last_name: '', email: '', waitlist_email_consent: false });

export default function WaitlistForm({ ctaLabel = WAITLIST.cta }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [previewComplete, setPreviewComplete] = useState(false);
  const requestIdRef = useRef(null);
  const pendingRef = useRef(false);
  const startedRef = useRef(false);

  // Held until an accepted response. A retry after a server failure replays the
  // same request id, so the backend recognises it and cannot store the person
  // twice.
  if (!requestIdRef.current) requestIdRef.current = createLeadRequestId();

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setSubmitError('');
  };

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackFormStart({ form_name: 'membership_waitlist', lead_source: WAITLIST.leadSource });
  };

  const validate = () => {
    const next = {};
    if (!form.first_name.trim()) next.first_name = 'Enter your first name.';
    if (!form.email.trim()) next.email = 'Enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Enter a valid email address.';
    if (!form.waitlist_email_consent) {
      next.waitlist_email_consent = 'Tick the box to agree to email updates about the waitlist.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (pendingRef.current || !validate()) return;
    pendingRef.current = true;
    setLoading(true);
    setSubmitError('');
    try {
      if (isEffectivePreview()) {
        setForm(emptyForm());
        setPreviewComplete(true);
        return;
      }
      await createLead(buildWaitlistLeadPayload({
        form: { ...form, location: GYM_CONFIG.location },
        source: WAITLIST.leadSource,
        attribution: getLeadAttribution(),
        requestId: requestIdRef.current,
      }));
      requestIdRef.current = createLeadRequestId();
      setJoined(true);
    } catch (error) {
      setSubmitError(getLeadSubmissionErrorMessage(
        error,
        'We could not add you to the waitlist. Please try again or call the gym.',
      ));
    } finally {
      pendingRef.current = false;
      setLoading(false);
    }
  };

  if (previewComplete) {
    return (
      <div role="status" className="scs-preview-success" data-testid="preview-waitlist-success">
        <Check size={26} aria-hidden="true" />
        <h3>Preview test complete</h3>
        <p>Nothing was stored and no email was sent. Production mode uses the approved lead endpoint.</p>
        <button type="button" className="scs-button scs-button-secondary" onClick={() => setPreviewComplete(false)}>
          Test the form again
        </button>
      </div>
    );
  }

  if (joined) {
    return (
      <div role="status" data-testid="waitlist-success">
        <h3 className="font-display text-2xl m-0 mb-3" style={{ color: 'var(--scs-forest)' }}>
          You are on the waitlist.
        </h3>
        <p className="text-base leading-relaxed m-0" style={{ color: 'var(--scs-text)' }}>
          We will email you when we have updates about membership availability. No payment has been taken,
          and you have not signed up for a paid membership.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} onFocusCapture={markStarted} noValidate data-testid="waitlist-form">
      {isEffectivePreview() && (
        <div className="scs-preview-notice" role="note" data-testid="preview-waitlist-notice">
          Preview test mode. Use test information only. Nothing entered here will be sent or stored.
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="waitlist-first-name" className="block text-base font-bold mb-2" style={{ color: 'var(--scs-ink)' }}>
            First name <span aria-hidden="true">*</span>
          </label>
          <input id="waitlist-first-name" value={form.first_name} autoComplete="given-name"
            onChange={(event) => update('first_name', event.target.value)}
            aria-invalid={Boolean(errors.first_name)}
            aria-describedby={errors.first_name ? 'waitlist-first-name-error' : undefined}
            className="input-light" data-testid="waitlist-first-name-input" />
          {errors.first_name && (
            <p id="waitlist-first-name-error" role="alert" data-testid="waitlist-first-name-error"
              className="text-sm mt-1 font-semibold" style={{ color: 'var(--scs-coral-dark)' }}>{errors.first_name}</p>
          )}
        </div>

        <div>
          <label htmlFor="waitlist-last-name" className="block text-base font-bold mb-2" style={{ color: 'var(--scs-ink)' }}>
            Last name <span className="font-normal" style={{ color: 'var(--scs-text-muted)' }}>(optional)</span>
          </label>
          <input id="waitlist-last-name" value={form.last_name} autoComplete="family-name"
            onChange={(event) => update('last_name', event.target.value)}
            className="input-light" data-testid="waitlist-last-name-input" />
        </div>

        <div>
          <label htmlFor="waitlist-email" className="block text-base font-bold mb-2" style={{ color: 'var(--scs-ink)' }}>
            Email address <span aria-hidden="true">*</span>
          </label>
          <input id="waitlist-email" type="email" inputMode="email" value={form.email} autoComplete="email"
            onChange={(event) => update('email', event.target.value)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'waitlist-email-error' : undefined}
            className="input-light" data-testid="waitlist-email-input" />
          {errors.email && (
            <p id="waitlist-email-error" role="alert" data-testid="waitlist-email-error"
              className="text-sm mt-1 font-semibold" style={{ color: 'var(--scs-coral-dark)' }}>{errors.email}</p>
          )}
        </div>

        <label className="flex items-start gap-3 cursor-pointer" htmlFor="waitlist-email-consent">
          <input id="waitlist-email-consent" type="checkbox" checked={form.waitlist_email_consent}
            onChange={(event) => update('waitlist_email_consent', event.target.checked)}
            aria-invalid={Boolean(errors.waitlist_email_consent)}
            aria-describedby={errors.waitlist_email_consent ? 'waitlist-consent-error' : undefined}
            className="mt-0.5 w-5 h-5 shrink-0 cursor-pointer" style={{ accentColor: 'var(--scs-forest)' }}
            data-testid="waitlist-consent-checkbox" />
          <span className="text-sm leading-relaxed" style={{ color: 'var(--scs-text)' }}>
            {WAITLIST.consent}{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2"
              data-testid="waitlist-privacy-link">Privacy Policy</a>.
          </span>
        </label>
        {errors.waitlist_email_consent && (
          <p id="waitlist-consent-error" role="alert" data-testid="waitlist-consent-error"
            className="text-sm font-semibold" style={{ color: 'var(--scs-coral-dark)' }}>{errors.waitlist_email_consent}</p>
        )}
      </div>

      {submitError && (
        <div role="alert" data-testid="waitlist-submit-error" className="mt-5 p-4 text-sm"
          style={{ border: '1px solid var(--scs-coral)', background: 'rgba(201,74,78,0.07)', color: 'var(--scs-coral-dark)', borderRadius: 'var(--scs-radius)' }}>
          {submitError} <a href={GYM_CONFIG.phoneHref} className="font-bold underline">Call {GYM_CONFIG.phone}</a>.
        </div>
      )}

      <button type="submit" disabled={loading} data-testid="waitlist-submit-button"
        className="btn-primary w-full text-base py-3.5 mt-6 disabled:opacity-50 disabled:cursor-not-allowed">
        {loading ? <><Loader2 size={17} className="animate-spin" aria-hidden="true" /> Adding you...</> : ctaLabel}
      </button>

      <p className="text-sm leading-relaxed mt-3 m-0" style={{ color: 'var(--scs-text-muted)' }}>
        No payment is required. Joining the waitlist does not commit you to a membership. We use your email
        only to send waitlist updates, and text messages are not part of the waitlist.
      </p>
    </form>
  );
}
